import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuthBootstrap } from './hooks/useAuth';
import { AppLayout } from './layouts/AppLayout';
import { GuestLayout } from './layouts/GuestLayout';

/**
 * Route-level code splitting (P3 #18).
 *
 * Each page is loaded as its own chunk via React.lazy + dynamic import.
 * The initial bundle previously crossed Vite's 500 KB warning threshold
 * because everything (Recharts on Dashboard, TradingView lib on Charts,
 * heavy modals, etc.) was eagerly imported.
 *
 * `Suspense` falls back to a thin centered message during the chunk
 * download — the visible interactive parts (sidebar nav, header) live
 * in AppLayout / GuestLayout which are still eagerly loaded.
 *
 * Login / Register / forgot-password are also lazy because they're hit
 * once per session and we want a fast TTI on the dashboard for the
 * common returning-user case.
 */
const Login          = lazy(() => import('./pages/Login')         .then(m => ({ default: m.Login })));
const Register       = lazy(() => import('./pages/Register')      .then(m => ({ default: m.Register })));
const EmailVerify    = lazy(() => import('./pages/EmailVerify')   .then(m => ({ default: m.EmailVerify })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword  = lazy(() => import('./pages/ResetPassword') .then(m => ({ default: m.ResetPassword })));
const KycSubmit      = lazy(() => import('./pages/KycSubmit')     .then(m => ({ default: m.KycSubmit })));
const Dashboard      = lazy(() => import('./pages/Dashboard')     .then(m => ({ default: m.Dashboard })));
const Portfolio      = lazy(() => import('./pages/Portfolio')     .then(m => ({ default: m.Portfolio })));
const Trade          = lazy(() => import('./pages/Trade')         .then(m => ({ default: m.Trade })));
const Trades         = lazy(() => import('./pages/Trades')        .then(m => ({ default: m.Trades })));
const TradeDetail    = lazy(() => import('./pages/TradeDetail')   .then(m => ({ default: m.TradeDetail })));
const Positions      = lazy(() => import('./pages/Positions')     .then(m => ({ default: m.Positions })));
const Charts         = lazy(() => import('./pages/Charts')        .then(m => ({ default: m.Charts })));
const OptionChain    = lazy(() => import('./pages/OptionChain')   .then(m => ({ default: m.OptionChain })));
const OpenInterest   = lazy(() => import('./pages/OpenInterest')  .then(m => ({ default: m.OpenInterest })));
const PredictionReview = lazy(() => import('./pages/PredictionReview').then(m => ({ default: m.PredictionReview })));
const Signals        = lazy(() => import('./pages/Signals')       .then(m => ({ default: m.Signals })));
const Strategies     = lazy(() => import('./pages/Strategies')    .then(m => ({ default: m.Strategies })));
const Watchlists     = lazy(() => import('./pages/Watchlists')    .then(m => ({ default: m.Watchlists })));
const Settings       = lazy(() => import('./pages/Settings')      .then(m => ({ default: m.Settings })));
const Profile        = lazy(() => import('./pages/Profile')       .then(m => ({ default: m.Profile })));
const Billing        = lazy(() => import('./pages/Billing')       .then(m => ({ default: m.Billing })));
const Upgrade        = lazy(() => import('./pages/Upgrade')       .then(m => ({ default: m.Upgrade })));
const News           = lazy(() => import('./pages/News')          .then(m => ({ default: m.News })));
const NewsSignal     = lazy(() => import('./pages/NewsSignal')    .then(m => ({ default: m.NewsSignal })));
const Help           = lazy(() => import('./pages/Help')          .then(m => ({ default: m.Help })));
const NotFound       = lazy(() => import('./pages/NotFound')      .then(m => ({ default: m.NotFound })));

function PageLoading() {
  return (
    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

export function App() {
  useAuthBootstrap();

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route element={<GuestLayout />}>
            <Route path="/login"            element={<Login />} />
            <Route path="/register"         element={<Register />} />
            <Route path="/verify-email"     element={<EmailVerify />} />
            <Route path="/forgot-password"  element={<ForgotPassword />} />
            <Route path="/reset-password"   element={<ResetPassword />} />
            {/* Alias matching the URL inside ResetPasswordMail (older mails). */}
            <Route path="/password/reset"   element={<ResetPassword />} />
          </Route>
          <Route element={<AppLayout />}>
            <Route path="/"           element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard"  element={<Dashboard />} />
            <Route path="/portfolio"  element={<Portfolio />} />
            <Route path="/kyc"        element={<KycSubmit />} />
            <Route path="/trade"      element={<Trade />} />
            <Route path="/trades"     element={<Trades />} />
            <Route path="/trades/:id" element={<TradeDetail />} />
            <Route path="/positions"  element={<Positions />} />
            <Route path="/charts"     element={<Charts />} />
            <Route path="/option-chain" element={<OptionChain />} />
            <Route path="/open-interest" element={<OpenInterest />} />
            <Route path="/prediction-review" element={<PredictionReview />} />
            <Route path="/strategies" element={<Strategies />} />
            <Route path="/signals"    element={<Signals />} />
            <Route path="/watchlists" element={<Watchlists />} />
            <Route path="/settings"   element={<Settings />} />
            <Route path="/profile"    element={<Profile />} />
            <Route path="/billing"    element={<Billing />} />
            <Route path="/upgrade"    element={<Upgrade />} />
            <Route path="/news"       element={<News />} />
            <Route path="/news-signal" element={<NewsSignal />} />
            <Route path="/help"       element={<Help />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
